<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import General from "@/services/General";
import modalAddApplicationLink from "@/components/modals/app/modalAddApplicationLink";
import modalEditApplicationLink from "@/components/modals/app/modalEditApplicationLink";
import Swal from "sweetalert2";
import Sortable from "sortablejs";

export default {
  components: { Layout, PageHeader, modalAddApplicationLink, modalEditApplicationLink },
  page: {
    title: "Application Links",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "Application Links",
      items: [{ text: "Links", active: true }],

      isBusy: false,
      error: null,
      tableData: [],
      totalItems: 0,

      selectedPlacement: null,
      placementOptions: [],

      editLinkData: null,

      topSortable: null,
      childSortables: {},

      fields: [
        { key: "title", label: "Title", col: "links-col-title" },
        { key: "subtitle", label: "Subtitle", col: "links-col-subtitle" },
        { key: "type", label: "Type", col: "links-col-type" },
        { key: "placements", label: "Placement", col: "links-col-placement" },
        { key: "url", label: "URL", col: "links-col-url" },
        { key: "timestamp", label: "Timestamp", col: "links-col-timestamp" },
        { key: "actions", label: "Action", col: "links-col-action" },
      ],
    };
  },
  computed: {
    topLevelItems() {
      return this.tableData
        .filter((l) => !l.parent)
        .sort((a, b) => a.order - b.order);
    },
    totalRows() {
      return this.totalItems || this.tableData.length;
    },
    placementFilterOptions() {
      const options = [{ value: null, text: "All placements" }];
      this.placementOptions.forEach((placement) => {
        options.push({ value: placement, text: placement });
      });
      return options;
    },
  },
  mounted() {
    this.loadPlacementOptions();
    this.fetchLinks();
  },
  beforeDestroy() {
    this.destroySortables();
  },
  methods: {
    modalAddApplicationLink() {
      this.$bvModal.show("add_application_link");
    },
    openEditLink(item) {
      this.editLinkData = {
        id: item.id,
        type: item.type,
        parent: item.parent || null,
      };
      this.$bvModal.show("edit_application_link");
    },

    destroySortables() {
      if (this.topSortable) {
        this.topSortable.destroy();
        this.topSortable = null;
      }
      Object.keys(this.childSortables).forEach((key) => {
        this.childSortables[key].destroy();
      });
      this.childSortables = {};
    },

    getChildren(categoryId) {
      return this.tableData
        .filter((l) => l.parent === categoryId)
        .sort((a, b) => a.order - b.order);
    },

    formatPlacements(placements) {
      if (!placements) {
        return "";
      }
      if (Array.isArray(placements)) {
        return placements.filter(Boolean).join(", ");
      }
      return String(placements);
    },

    async loadPlacementOptions() {
      try {
        const response = await General.getAvailableApplicationLinkPlacements();
        const data = response.data?.data ?? [];
        this.placementOptions = Array.isArray(data) ? data : [];
      } catch (e) {
        this.placementOptions = [];
      }
    },

    async fetchLinks() {
      try {
        this.error = null;
        this.isBusy = true;
        this.destroySortables();

        const response = await General.getApplicationLinks(
          1,
          1000,
          this.selectedPlacement
        );
        const payload = response && response.data ? response.data : {};
        this.tableData = Array.isArray(payload.links) ? payload.links : [];
        this.totalItems = payload.total_items ?? this.tableData.length;

        this.$nextTick(() => {
          this.initSortable();
        });
      } catch (e) {
        this.error =
          e?.response?.data?.error || e?.message || "Failed to load links.";
        this.tableData = [];
        this.totalItems = 0;
      } finally {
        this.isBusy = false;
      }
    },

    buildFullPayload() {
      const items = [];

      this.tableData
        .filter((l) => !l.parent)
        .sort((a, b) => a.order - b.order)
        .forEach((item, idx) => {
          item.order = idx + 1;
          items.push({ id: item.id, parent: null, order: item.order });
        });

      this.tableData
        .filter((l) => l.type === "category" && !l.parent)
        .forEach((cat) => {
          this.tableData
            .filter((l) => l.parent === cat.id)
            .sort((a, b) => a.order - b.order)
            .forEach((child, idx) => {
              child.parent = cat.id;
              child.order = idx + 1;
              items.push({ id: child.id, parent: cat.id, order: child.order });
            });
        });

      return items;
    },

    async persistReorder(items) {
      try {
        const response = await General.reorderApplicationLinks({ items });
        if (response.data && response.data.success) {
          await this.fetchLinks();
        } else {
          const error = response.data?.error || "Failed to reorder links.";
          Swal.fire("Error", error, "error");
          await this.fetchLinks();
        }
      } catch (error) {
        const errorMsg =
          error?.response?.data?.error || "Failed to reorder links.";
        Swal.fire("Error", errorMsg, "error");
        await this.fetchLinks();
      }
    },

    handleTopSortEnd(evt) {
      if (
        evt.oldIndex == null ||
        evt.newIndex == null ||
        evt.oldIndex === evt.newIndex
      ) {
        return;
      }

      const parent = evt.from;
      const children = parent.children;
      if (evt.oldIndex < evt.newIndex) {
        parent.insertBefore(evt.item, children[evt.oldIndex]);
      } else {
        parent.insertBefore(evt.item, children[evt.oldIndex + 1]);
      }

      const topLevel = this.tableData
        .filter((l) => !l.parent)
        .sort((a, b) => a.order - b.order);
      const moved = topLevel.splice(evt.oldIndex, 1)[0];
      topLevel.splice(evt.newIndex, 0, moved);
      topLevel.forEach((item, idx) => {
        item.order = idx + 1;
      });

      this.persistReorder(this.buildFullPayload());
    },

    handleChildSortEnd(evt, categoryId) {
      if (
        evt.oldIndex == null ||
        evt.newIndex == null ||
        evt.oldIndex === evt.newIndex
      ) {
        return;
      }

      const parent = evt.from;
      const children = parent.children;
      if (evt.oldIndex < evt.newIndex) {
        parent.insertBefore(evt.item, children[evt.oldIndex]);
      } else {
        parent.insertBefore(evt.item, children[evt.oldIndex + 1]);
      }

      const siblings = this.tableData
        .filter((l) => l.parent === categoryId)
        .sort((a, b) => a.order - b.order);
      const moved = siblings.splice(evt.oldIndex, 1)[0];
      siblings.splice(evt.newIndex, 0, moved);
      siblings.forEach((item, idx) => {
        item.order = idx + 1;
      });

      this.persistReorder(this.buildFullPayload());
    },

    initSortable() {
      this.destroySortables();

      const topEl = document.getElementById("top-level-list");
      if (topEl) {
        this.topSortable = Sortable.create(topEl, {
          handle: ".drag-handle-top",
          draggable: ".top-level-item",
          animation: 150,
          ghostClass: "sortable-ghost",
          onEnd: this.handleTopSortEnd,
        });
      }

      this.topLevelItems
        .filter((i) => i.type === "category")
        .forEach((cat) => {
          const el = document.getElementById(`category-children-${cat.id}`);
          if (!el) {
            return;
          }
          this.childSortables[cat.id] = Sortable.create(el, {
            handle: ".drag-handle-child",
            draggable: ".child-item",
            animation: 150,
            ghostClass: "sortable-ghost",
            onEnd: (evt) => this.handleChildSortEnd(evt, cat.id),
          });
        });
    },

    deleteLink(id) {
      Swal.fire({
        title: "Delete application link?",
        text: "This link will be permanently removed.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await General.deleteApplicationLink(id);
            if (response.data && response.data.success) {
              await this.fetchLinks();
              Swal.fire(
                "Success!",
                "Application link deleted successfully.",
                "success"
              );
            } else {
              const error = response.data?.error || "Failed";
              Swal.fire("Fail!", error, "warning");
            }
          } catch (error) {
            const errorMsg =
              error?.response?.data?.error ||
              "An error occurred while deleting the link.";
            Swal.fire("Error", errorMsg, "error");
          }
        }
      });
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-0">
              <div class="col-12">
                <div class="d-flex align-items-center flex-wrap gap-3">
                  <a
                    href="javascript:void(0);"
                    class="btn btn-success waves-effect waves-light"
                    @click="modalAddApplicationLink"
                  >
                    <i class="mdi mdi-plus me-2"></i>
                    Add Link
                  </a>
                  <div class="d-flex align-items-center">
                    <label class="me-2 mb-0 text-nowrap">Placement:</label>
                    <b-form-select
                      v-model="selectedPlacement"
                      :options="placementFilterOptions"
                      size="sm"
                      style="width: auto; min-width: 160px;"
                      @change="fetchLinks"
                    ></b-form-select>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger mt-3">
              {{ error }}
            </div>

            <div class="row mt-2 mb-2">
              <div class="col d-inline-flex align-items-center">
                <h4 class="card-title m-0">Total Links: {{ totalRows }}</h4>
              </div>
            </div>

            <div v-if="isBusy" class="text-center my-4">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
            </div>

            <div v-else id="application_links_table" class="application-links-table">
              <div
                id="table_header"
                class="links-grid links-grid--header px-3 py-2 mb-3"
                v-if="tableData.length"
              >
                <div class="links-col-drag" aria-hidden="true">
                  <i class="uil uil-bars links-drag-icon links-drag-icon--hidden"></i>
                </div>
                <div
                  v-for="field in fields"
                  :key="field.key"
                  :class="field.col"
                >
                  {{ field.label }}
                </div>
              </div>

              <div v-if="!tableData.length" class="text-muted text-center py-4">
                No Data Found
              </div>

              <div id="top-level-list">
                <div
                  v-for="item in topLevelItems"
                  :key="item.id"
                  :data-id="item.id"
                  class="top-level-item mb-3"
                >
                  <div v-if="item.type === 'category'" class="card category-card">
                    <div class="category-header px-3 links-grid">
                      <div class="links-col-drag drag-handle-top">
                        <i class="uil uil-bars links-drag-icon"></i>
                      </div>

                      <div class="links-col-title">
                        <p class="text-muted m-0 fw-semibold text-truncate">{{ item.title }}</p>
                        <span class="badge bg-soft-primary text-primary font-size-11"
                          >Category</span
                        >
                      </div>

                      <div class="links-col-subtitle">
                        <p class="text-muted m-0 text-truncate">{{ item.subtitle || "—" }}</p>
                      </div>

                      <div class="links-col-type">
                        <p class="text-muted m-0">{{ item.type }}</p>
                      </div>

                      <div class="links-col-placement">
                        <p class="text-muted m-0 text-truncate">
                          {{ formatPlacements(item.placements) || "—" }}
                        </p>
                      </div>

                      <div class="links-col-url">
                        <span class="text-muted">—</span>
                      </div>

                      <div class="links-col-timestamp">
                        <p class="text-muted m-0 text-truncate">{{ item.timestamp || "—" }}</p>
                      </div>

                      <div class="links-col-action">
                        <a
                          href="javascript:void(0);"
                          class="text-primary me-2"
                          title="Edit"
                          @click="openEditLink(item)"
                        >
                          <i class="uil uil-pen font-size-18"></i>
                        </a>
                        <a
                          href="javascript:void(0);"
                          class="text-danger"
                          title="Delete"
                          @click="deleteLink(item.id)"
                        >
                          <i class="uil uil-trash-alt font-size-18"></i>
                        </a>
                      </div>
                    </div>

                    <div class="category-body px-3 pb-3">
                      <div
                        :id="'category-children-' + item.id"
                        class="category-children-list mt-2"
                        :class="{ 'category-children-list--empty': !getChildren(item.id).length }"
                      >
                <div
                    v-for="child in getChildren(item.id)"
                       :key="child.id"
                         :data-id="child.id"
                          class="child-item"
                        >
                          <div class="child-row links-grid">
                            <div class="links-col-drag drag-handle-child">
                              <i class="uil uil-bars links-drag-icon"></i>
                            </div>

                            <div class="links-col-title">
                              <p class="text-muted m-0 text-truncate">{{ child.title }}</p>
                            </div>

                            <div class="links-col-subtitle">
                              <p class="text-muted m-0 text-truncate">
                                {{ child.subtitle || "—" }}
                              </p>
                            </div>

                 <div class="links-col-type">
                              <p class="text-muted m-0">{{ child.type }}</p>
                            </div>

               <div class="links-col-placement">
                      <p class="text-muted m-0">—</p>
                            </div>

                            <div class="links-col-url">
                              <a
                                v-if="child.url"
                                :href="child.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-truncate d-block"
                                >{{ child.url }}</a
                              >
                              <span v-else class="text-muted">—</span>
                            </div>

                            <div class="links-col-timestamp">
                              <p class="text-muted m-0 text-truncate">
                                {{ child.timestamp || "—" }}
                              </p>
                            </div>

                            <div class="links-col-action">
                              <a
                                href="javascript:void(0);"
                                class="text-primary me-2"
                                title="Edit"
                                @click="openEditLink(child)"
                              >
                                <i class="uil uil-pen font-size-18"></i>
                              </a>
                              <a
                                href="javascript:void(0);"
                                class="text-danger"
                                title="Delete"
                                @click="deleteLink(child.id)"
                              >
                                <i class="uil uil-trash-alt font-size-18"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      <p
                        v-if="!getChildren(item.id).length"
                        class="text-muted font-size-12 mb-0 mt-2"
                      >
                        No links in this category
                      </p>
                    </div>
                  </div>

                  <!-- Standalone link card -->
                  <div v-else class="card px-3 links-grid links-grid--standalone">
                    <div class="links-col-drag drag-handle-top">
                      <i class="uil uil-bars links-drag-icon"></i>
                    </div>

                    <div class="links-col-title">
                      <p class="text-muted m-0 fw-semibold text-truncate">{{ item.title }}</p>
                    </div>

                    <div class="links-col-subtitle">
                      <p class="text-muted m-0 text-truncate">{{ item.subtitle || "—" }}</p>
                    </div>

                    <div class="links-col-type">
                      <p class="text-muted m-0">{{ item.type }}</p>
                    </div>
  <div class="links-col-placement">
         <p class="text-muted m-0 text-truncate">
                        {{ formatPlacements(item.placements) || "—" }}
                      </p>
                    </div>

                    <div class="links-col-url">
                      <a
                        v-if="item.url"
                        :href="item.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-truncate d-block"
                        >{{ item.url }}</a
                      >
                      <span v-else class="text-muted">—</span>
                    </div>

                    <div class="links-col-timestamp">
                      <p class="text-muted m-0 text-truncate">{{ item.timestamp || "—" }}</p>
                    </div>

                    <div class="links-col-action">
                      <a
                        href="javascript:void(0);"
                        class="text-primary me-2"
                        title="Edit"
                        @click="openEditLink(item)"
                      >
                        <i class="uil uil-pen font-size-18"></i>
                      </a>
                      <a
                        href="javascript:void(0);"
                        class="text-danger"
                        title="Delete"
                        @click="deleteLink(item.id)"
                      >
                        <i class="uil uil-trash-alt font-size-18"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modalAddApplicationLink @refreshScreen="fetchLinks" />
    <modalEditApplicationLink :linkData="editLinkData" @refreshScreen="fetchLinks" />
  </Layout>
</template>

<style scoped>
.application-links-table {
  overflow-x: auto;
}
.links-grid {
  display: grid;
  grid-template-columns:
    28px
    minmax(110px, 1.3fr)
    minmax(100px, 1.1fr)
    72px
    100px
    minmax(140px, 2fr)
    minmax(130px, 1.1fr)
    64px;
  column-gap: 12px;
  align-items: center;
  width: 100%;
  min-width: 960px;
}
.links-grid--header {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #74788d;
  border-top: 1px solid #f5f6f8;
  border-bottom: 1px solid #f5f6f8;
}
.links-grid--header > [class^="links-col-"] {
  white-space: nowrap;
}
.links-grid--standalone {
  padding-top: 12px;
  padding-bottom: 12px;
}
.links-col-drag {
  display: flex;
  align-items: center;
  justify-content: center;
}
.links-drag-icon {
  font-size: 18px;
  color: #6c757d;
  cursor: move;
}
.links-drag-icon--hidden {
  visibility: hidden;
}
.links-col-title,
.links-col-subtitle,
.links-col-url,
.links-col-timestamp,
.links-col-placement {
  min-width: 0;
}
.links-col-action {
  text-align: center;
  white-space: nowrap;
}
.drag-handle-top,
.drag-handle-child {
  cursor: move;
}
.child-row {
  border-left: 3px solid #dee2e6;
  padding-top: 10px;
  padding-bottom: 10px;
  border-top: 1px solid #f1f3f5;
  background-color: #fff;
}
.category-card {
  border-left: 3px solid #556ee6;
}
.category-header {
  background-color: #f8f9fa;
  padding-top: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f7;
}
.category-body {
  background-color: #fff;
}
.category-children-list--empty {
  min-height: 36px;
  border: 1px dashed #dee2e6;
  border-radius: 4px;
}
.sortable-ghost {
  opacity: 0.4;
}
</style>
